import type { SalesLookupVariant } from 'apis/sales-lookup';
import { Box, Flex, Switch, Text } from 'design-system';
import type { FlexibilityFlags } from 'pages/sales-lookup/utils/build-inquiry-payload';
import { packagingLabel } from 'pages/sales-lookup/utils/variants';

interface FlexibilityTogglesProps {
  variant: SalesLookupVariant;
  value: FlexibilityFlags;
  onChange: (key: keyof FlexibilityFlags, open: boolean) => void;
}

// "Open to switch" per dimension, the same five the CMS item wizard offers
// (brand / origin / state / spec / packaging). Each row names the variant's
// own value so the rep can ask the client "would you take another brand?"
// while looking at the one they quoted. Thumb-sized switches, not the wizard's
// checkbox grid — this is a phone screen.
export const FlexibilityToggles = ({ variant, value, onChange }: FlexibilityTogglesProps) => {
  const rows: Array<{ key: keyof FlexibilityFlags; label: string; text: string }> = [
    { key: 'brandFlexible', label: 'Brand', text: variant.brand ?? 'No brand' },
    { key: 'originFlexible', label: 'Origin', text: variant.origin ?? 'Unknown' },
    { key: 'stateFlexible', label: 'State', text: variant.state ?? 'Unspecified' },
    { key: 'specFlexible', label: 'Spec', text: variant.spec ?? 'Unspecified' },
    { key: 'packagingFlexible', label: 'Packaging', text: packagingLabel(variant) },
  ];

  return (
    <Box>
      <Text fontSize='10.5px' fontWeight='semibold' letterSpacing='0.1em' color='gray.500' mb='6px'>
        OPEN TO SWITCH
      </Text>
      {rows.map((row) => (
        <Flex
          key={row.key}
          as='label'
          align='center'
          justify='space-between'
          py='8px'
          borderBottom='1px solid'
          borderColor='gray.100'
          cursor='pointer'>
          <Box minW='0' pr='12px'>
            <Text fontSize='11px' color='gray.500'>
              {row.label}
            </Text>
            <Text fontSize='13.5px' color='gray.800' noOfLines={1}>
              {row.text}
            </Text>
          </Box>
          <Switch colorScheme='red' isChecked={value[row.key]} onChange={(e) => onChange(row.key, e.target.checked)} />
        </Flex>
      ))}
    </Box>
  );
};
